import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAdminStore } from '../../store/adminStore'
import { tokens, Button, Field } from './_ui'

const ADMIN_LANGS = ['ru', 'en', 'uz'] as const

const NAV = [
  { to: '/admin/shops', label: 'Shops', icon: '🏪' },
  { to: '/admin/users', label: 'Users', icon: '👤' },
  { to: '/admin/analytics', label: 'Stats', icon: '📊' },
  { to: '/admin/bots', label: 'Bots', icon: '🤖' },
  { to: '/admin/invite-codes', label: 'Keys', icon: '🔑' },
]

export default function AdminLayout() {
  const { apiKey, setApiKey, clearApiKey } = useAdminStore()
  const { i18n } = useTranslation()
  const [keyInput, setKeyInput] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    const saved = localStorage.getItem('crowd_admin_lang')
    if (saved && ADMIN_LANGS.includes(saved as typeof ADMIN_LANGS[number])) {
      i18n.changeLanguage(saved)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLangCycle = () => {
    const idx = ADMIN_LANGS.indexOf(i18n.language as typeof ADMIN_LANGS[number])
    const next = ADMIN_LANGS[(idx + 1) % ADMIN_LANGS.length]
    i18n.changeLanguage(next)
    localStorage.setItem('crowd_admin_lang', next)
  }

  const handleSetKey = () => {
    if (keyInput.trim()) {
      setApiKey(keyInput.trim())
      navigate('/admin/shops')
    }
  }

  // ── Login screen ────────────────────────────────────────────────
  if (!apiKey) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 safe-area-bottom"
        style={{ background: tokens.pageBg }}
      >
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                boxShadow: '0 10px 32px -8px rgba(139, 92, 246, 0.5)',
              }}
            >
              ✦
            </div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: tokens.text,
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              Backoffice
            </h1>
            <p style={{ fontSize: 13, opacity: 0.5, color: tokens.text, marginTop: 6 }}>
              Sign in with your admin key
            </p>
          </div>
          <div style={{ background: tokens.cardBg, borderRadius: 18, padding: 18 }}>
            <Field
              label="Admin key"
              value={keyInput}
              onChange={setKeyInput}
              placeholder="••••••••••••"
              type="password"
            />
            <Button onClick={handleSetKey} disabled={!keyInput.trim()} style={{ width: '100%', marginTop: 4 }}>
              Continue →
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ── Main layout ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen safe-area-bottom" style={{ background: tokens.pageBg }}>
      {/* Sticky top bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: tokens.pageBg,
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px 10px',
            maxWidth: 1100,
            margin: '0 auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #6366f1, #ec4899)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                color: 'white',
              }}
            >
              ✦
            </div>
            <h1
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: tokens.text,
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              Backoffice
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={handleLangCycle}
              style={{
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'monospace',
                textTransform: 'uppercase',
                padding: '6px 10px',
                borderRadius: 8,
                background: tokens.cardBg,
                color: tokens.text,
                opacity: 0.6,
                border: 'none',
                cursor: 'pointer',
              }}
              title="Cycle language"
            >
              {i18n.language}
            </button>
            <button
              onClick={() => {
                clearApiKey()
                navigate('/admin')
              }}
              style={{
                fontSize: 11,
                padding: '6px 10px',
                borderRadius: 8,
                background: tokens.cardBg,
                color: tokens.text,
                opacity: 0.5,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Nav pills */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: '0 16px 12px',
            overflowX: 'auto',
            maxWidth: 1100,
            margin: '0 auto',
          }}
        >
          {NAV.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                background: isActive ? tokens.text : tokens.cardBg,
                color: isActive
                  ? 'var(--app-page-bg)'
                  : tokens.text,
                opacity: isActive ? 1 : 0.85,
                letterSpacing: '-0.005em',
                transition: 'background 0.15s',
              })}
            >
              <span style={{ fontSize: 14 }}>{icon}</span>
              {label}
            </NavLink>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <Outlet />
      </div>
    </div>
  )
}
